import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronsUpDown, Check, X } from 'lucide-react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';

const normalize = (str) => str.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

export function SearchableCombobox({
  open,
  onOpenChange,
  items = [],
  selectedValue,
  onSelect,
  onClear,
  placeholder = 'Selecione...',
  searchPlaceholder = 'Digite para buscar...',
  emptyMessage = 'Nenhum resultado encontrado.',
  icon: Icon,
  disabled = false,
  displayValue,
  getItemKey,
  getItemLabel,
  getItemSearchValue,
  isSelected,
  className = '',
  testId = '',
}) {
  const [search, setSearch] = useState('');
  const inputRef = useRef(null);

  // Reset search when popover closes
  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  const uniqueItems = useMemo(() => {
    const seen = new Set();
    return items.filter((item) => {
      const label = getItemLabel(item);
      const key = normalize(String(label || ''));
      if (!key) return true;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [items, getItemLabel]);

  const filtered = search
    ? uniqueItems.filter(item => {
        const searchVal = getItemSearchValue ? getItemSearchValue(item) : getItemLabel(item);
        return normalize(searchVal).includes(normalize(search));
      })
    : uniqueItems;

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={`h-10 md:h-12 rounded-xl border-slate-200 bg-white shadow-sm w-full justify-between font-normal text-sm hover:bg-white disabled:opacity-50 ${className}`}
          data-testid={testId}
        >
          <div className="flex items-center gap-2 truncate">
            {Icon && <Icon className="w-4 h-4 text-slate-400 flex-shrink-0" />}
            <span className={selectedValue ? 'text-slate-900 truncate' : 'text-slate-500'}>
              {displayValue || placeholder}
            </span>
          </div>
          {selectedValue && onClear ? (
            <X
              className="w-4 h-4 text-slate-400 hover:text-slate-600 flex-shrink-0"
              onClick={(e) => { e.stopPropagation(); onClear(); }}
            />
          ) : (
            <ChevronsUpDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="z-[80] w-[--radix-popover-trigger-width] p-0" align="start" side="bottom" sideOffset={4}>
        <Command shouldFilter={false}>
          <CommandInput
            ref={inputRef}
            placeholder={searchPlaceholder}
            value={search}
            onValueChange={setSearch}
            data-testid={testId ? `${testId}-search` : undefined}
          />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup className="max-h-[250px] overflow-y-auto">
              {filtered.map((item) => {
                const key = getItemKey(item);
                const label = getItemLabel(item);
                const selected = isSelected ? isSelected(item) : false;
                return (
                  <CommandItem
                    key={key}
                    value={label}
                    onSelect={() => {
                      onSelect(item);
                      onOpenChange(false);
                      setSearch('');
                    }}
                    data-testid={testId ? `${testId}-item-${key}` : undefined}
                  >
                    <Check className={`mr-2 h-4 w-4 ${selected ? 'opacity-100' : 'opacity-0'}`} />
                    <span className="truncate">{label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
